import { type FC, useEffect, useMemo } from "react";
import {
  createDateRange,
  horizontalBarChartMargins,
  noData,
  validLineData,
} from "features/Library/Common/utils/performanceUtils";
import { reportingColors } from "../../../../Library/Common/utils/constants";
import { ChartWrapper, HorizontalBarChart, LineChart, getDateFormatByCount } from "components/charts";
import CardReporting from "components/cards/CardReporting/CardReporting";
import { type ColumnOption } from "interfaces/columnOptions";
import { GenericPerformanceList } from "../../shared/GenericPerformanceList";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { TextTruncate } from "components";
import { Tooltip } from "components/common/tooltip";
import { useQuery } from "@tanstack/react-query";
import { sharedAccountReportingHorizontalBarProps, sharedAccountReportingLineProps } from "../../shared";
import { type CommonAccountReportPropsExtended } from "../../types";
import { EntityType } from "features/Library/Flows/Designer/types";

import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import { type AppDispatch } from "features/Application/globaltypes/redux";
import { type ConnectedProps, connect } from "react-redux";
import { setExportAction, setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { bindAction } from "interfaces";
import { bindActionCreators } from "@reduxjs/toolkit";
import { setIsReportEnabled, reset } from "features/Reporting/state/toolbar/toolbarSlice";
import { doPhishingCampaignExport } from "features/Reporting/state/content/contentActions";

import styles from "../../contentOverviewReports.module.scss";
import phishingStyles from "./phishingCampaignsReport.module.scss";
import "features/Library/Common/utils/performanceSCSSUtils.scss";
import { type PhishingCampaignDetails } from "../../../types/content";
import { fetchDailyData, fetchEngagementQuery, fetchChartQuery, fetchDetailsQuery } from "./fetchDailyData";
import cn from "classnames";
import { ThreatDefenseEmailStatusEnum } from "features/Library/SimulatedPhishing/Campaigns/Details/types";
import { CHART_PERIOD, useChartPeriodMeasure } from "hooks/useChartPeriodMeasure";

const activityColorScale = [
  reportingColors.blue,
  reportingColors.greenLight,
  reportingColors.yellow,
  reportingColors.red,
];

export type Props = CommonAccountReportPropsExtended & PropsFromRedux;

export const PhishingCampaignsReport: FC<Props> = ({
  filter,
  setSelectedContent,
  setHasExportData,
  setExportAction,
  phishingExport,
  setIsReportEnabled,
  resetIsReportEnabled,
}) => {
  const reportEnabled = useFeatureFlag(FeatureFlags.PhishingCampaignsReport);

  const dateRange = useMemo(() => createDateRange(filter.dateFrom, filter.dateTo), [filter.dateFrom, filter.dateTo]);

  const [chartPeriod, measureRef] = useChartPeriodMeasure(
    dateRange,
    sharedAccountReportingLineProps.margins!.left + sharedAccountReportingLineProps.margins!.right,
  );

  const lineChartQuery = useQuery({
    queryKey: ["PhishingCampaignReportDailyData", { ...filter, aggregation: chartPeriod }],
    queryFn: fetchDailyData,
    enabled: reportEnabled && chartPeriod !== CHART_PERIOD.UNSET && filter.type !== undefined,
  });

  const engagementQuery = useQuery({
    queryKey: ["PhishingCampaignReportEngagement", filter],
    queryFn: fetchEngagementQuery,
    enabled: reportEnabled && filter.type !== undefined,
  });

  const chartQuery = useQuery({
    queryKey: ["PhishingCampaignReportVulnerability", filter],
    queryFn: fetchChartQuery,
    enabled: reportEnabled && filter.type !== undefined,
  });

  const detailsQuery = useQuery({
    queryKey: ["PhishingCampaignReportDetails", filter],
    queryFn: fetchDetailsQuery,
    enabled: reportEnabled && filter.type !== undefined,
  });

  useEffect(() => {
    setHasExportData({
      hasDataToExport: !!detailsQuery.data && detailsQuery.data.length > 0,
      tooltipContent: "No data available for export. There are no campaigns with activity data.",
    });
  }, [detailsQuery.data, setHasExportData]);

  useEffect(() => {
    setExportAction({
      method: phishingExport,
      args: [filter],
      isExportEnabled: true,
    });
  }, [phishingExport, filter, setExportAction]);

  useEffect(() => {
    setIsReportEnabled(reportEnabled);

    return () => {
      resetIsReportEnabled();
    };
  }, [reportEnabled, setIsReportEnabled, resetIsReportEnabled]);

  const columns: ColumnOption<PhishingCampaignDetails>[] = useMemo(
    () => [
      {
        name: "Name",
        width: 5,
        isSortable: false,
        render(item) {
          return (
            <Tooltip
              target={
                <button
                  className={styles.linkButton}
                  onClick={() => {
                    setSelectedContent({
                      id: item.CampaignId,
                      type: EntityType.Campaign,
                      name: item.CampaignName,
                    });
                  }}
                >
                  <TextTruncate lines={2} className={styles.wordBreak}>
                    {item.CampaignName}
                  </TextTruncate>
                </button>
              }
              content={item.CampaignName}
              showAlways
            />
          );
        },
      },
      {
        name: "Type",
        width: 3,
        isSortable: false,
        render(item) {
          return <div>{item.CampaignType}</div>;
        },
      },
      {
        isSortable: false,
        name: "Emails Sent",
        width: 2,
        render(item) {
          return <div>{item.EmailsSent.toLocaleString()}</div>;
        },
      },
      {
        isSortable: false,
        width: 2,
        name: "Opened",
        render(item) {
          return (
            <div>
              <span>{item.EmailsOpened.toLocaleString()} </span>
              <span className={phishingStyles.percentage}>
                {item.EmailsSent > 0 ? `(${Math.round((item.EmailsOpened / item.EmailsSent) * 100)}%)` : "(0%)"}
              </span>
            </div>
          );
        },
      },
      {
        isSortable: false,
        width: 2,
        name: "Clicked",
        render(item) {
          return (
            <div>
              <span>{item.EmailsClicked.toLocaleString()} </span>
              <span className={phishingStyles.percentage}>
                {item.EmailsSent > 0 ? `(${Math.round((item.EmailsClicked / item.EmailsSent) * 100)}%)` : "(0%)"}
              </span>
            </div>
          );
        },
      },
      {
        width: 2,
        render(item) {
          return (
            <div>
              <span>{item.DataSubmitted.toLocaleString()} </span>
              <span className={phishingStyles.percentage}>
                {item.EmailsSent > 0 ? `(${Math.round((item.DataSubmitted / item.EmailsSent) * 100)}%)` : "(0%)"}
              </span>
            </div>
          );
        },
        name: "Submitted Data",
        isSortable: false,
      },
    ],
    [setSelectedContent],
  );

  if (!reportEnabled) {
    return <ReportUnavailable />;
  }

  return (
    <div className={styles.reportBody}>
      <div className={styles.lineChartContainer} ref={measureRef}>
        <ChartWrapper
          titles={"Activity"}
          showLegend
          legendLabels={[
            ThreatDefenseEmailStatusEnum.SENT,
            ThreatDefenseEmailStatusEnum.OPENED,
            ThreatDefenseEmailStatusEnum.CLICKED,
            ThreatDefenseEmailStatusEnum.SUBMITTED,
          ]}
          colorRange={activityColorScale}
        >
          <RequestStatusRenderer state={lineChartQuery.status}>
            {lineChartQuery.isSuccess && validLineData(lineChartQuery.data.lineData.yData) ? (
              <LineChart
                {...sharedAccountReportingLineProps}
                xFormatterFunc={getDateFormatByCount(lineChartQuery.data.lineData.xData.length)}
                colorRange={activityColorScale}
                xData={lineChartQuery.data.lineData.xData}
                yData={lineChartQuery.data.lineData.yData}
              />
            ) : (
              noData(filter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>
      <div className={styles.funnelChartContainer}>
        <ChartWrapper titles={"Vulnerability Areas"}>
          <RequestStatusRenderer state={chartQuery.status}>
            {chartQuery.isSuccess && chartQuery.data.some((d) => d.value > 0) ? (
              <HorizontalBarChart
                {...sharedAccountReportingHorizontalBarProps}
                margins={{ ...horizontalBarChartMargins, left: 172 }}
                data={chartQuery.data}
              />
            ) : (
              noData(filter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>
      <div className={cn(styles.performanceCardSection, phishingStyles.cards)}>
        <RequestStatusRenderer state={engagementQuery.status}>
          {engagementQuery.isSuccess && <CardReporting items={engagementQuery.data} />}
        </RequestStatusRenderer>
      </div>
      <div className={styles.table}>
        <RequestStatusRenderer state={detailsQuery.status}>
          {detailsQuery.isSuccess && (
            <GenericPerformanceList title="Campaigns" rows={detailsQuery.data} columns={columns} filter={filter} />
          )}
        </RequestStatusRenderer>
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setHasExportData: bindAction(setHasDataToExport, dispatch),
  phishingExport: bindActionCreators(doPhishingCampaignExport, dispatch),
  setExportAction: bindAction(setExportAction, dispatch),
  setIsReportEnabled: bindAction(setIsReportEnabled, dispatch),
  resetIsReportEnabled: bindAction(reset, dispatch),
});

const connector = connect(null, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(PhishingCampaignsReport);
export default ConnectedComponent;
